/**
 * useFavoritesSync Hook
 *
 * Subscribes to user's favorites WebSocket topic and invalidates
 * TanStack Query cache when favorites change on other apps/tabs.
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useWebSocketOptional,
  useRealtimeEventOptional,
  EventType,
  RealtimeTopics,
  type FavoriteAddedPayload,
  type FavoriteRemovedPayload,
} from '@teletienda/ws-client/react';
import { useAuth } from '@teletienda/shared-auth-ui';

/**
 * Hook to sync favorites across apps via WebSocket
 *
 * Call this hook in FavoritesPage or at app root level.
 * Automatically subscribes to user's favorites topic and invalidates
 * queries when FAVORITE_ADDED or FAVORITE_REMOVED events are received.
 *
 * @example
 * ```tsx
 * function FavoritesPage() {
 *   useFavoritesSync(); // Subscribe to real-time updates
 *   const { data: favorites } = useFavorites();
 *   // ...
 * }
 * ```
 */
export function useFavoritesSync(): void {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();
  const { isConnected, subscribe, unsubscribe } = useWebSocketOptional();

  // Subscribe to user's favorites topic
  useEffect(() => {
    console.log('[useFavoritesSync] Effect running:', {
      isConnected,
      isAuthenticated,
      userGuid: user?.userGuid,
    });

    if (!isConnected || !isAuthenticated || !user?.userGuid) {
      console.log('[useFavoritesSync] Skipping subscription - not ready');
      return;
    }

    const topic = RealtimeTopics.userFavorites(user.userGuid);
    console.log('[useFavoritesSync] Subscribing to topic:', topic);
    subscribe([topic]);

    return () => {
      console.log('[useFavoritesSync] Unsubscribing from topic:', topic);
      unsubscribe([topic]);
    };
  }, [isConnected, isAuthenticated, user?.userGuid, subscribe, unsubscribe]);

  // Invalidate queries on events
  const invalidateFavorites = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['favorites'] });
    queryClient.invalidateQueries({ queryKey: ['favorites-check'] });
  }, [queryClient]);

  // Handle FAVORITE_ADDED events (use optional version - safe outside WebSocketProvider)
  useRealtimeEventOptional<FavoriteAddedPayload>(
    EventType.FAVORITE_ADDED,
    invalidateFavorites,
    [invalidateFavorites]
  );

  // Handle FAVORITE_REMOVED events (use optional version - safe outside WebSocketProvider)
  useRealtimeEventOptional<FavoriteRemovedPayload>(
    EventType.FAVORITE_REMOVED,
    invalidateFavorites,
    [invalidateFavorites]
  );
}
